import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { WorkerShell } from "@/components/Layout";
import { MapPin, Clock, CaretRight, HandGrabbing, Calendar, Lightning, Sun, MagnifyingGlass, X, Microphone, Broadcast, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useT } from "@/i18n";
import QuickJobModal from "@/components/QuickJobModal";
import QuickCaptureModal from "@/components/QuickCaptureModal";
import PrepSheet from "@/components/PrepSheet";
import VoiceToPunchModal from "@/components/VoiceToPunchModal";
import AITriageButton from "@/components/AITriageButton";
import PurgeFlagModal from "@/components/PurgeFlagModal";

import { todayLocalISO as todayISO } from "@/lib/dateHelpers";
const scheduledDate = (j) => {
  // Prefer sweeper_pin.date over scheduled_for — a WO pinned to today's
  // sweep should appear on today's list even if scheduled_for is empty.
  const pinned = j.sweeper_pin?.date || "";
  if (pinned) return pinned.slice(0, 10);
  return (j.scheduled_for || "").slice(0, 10);
};

export default function WorkerDashboard() {
  const { t } = useT();
  const statusMeta = {
    assigned: { label: t("worker.status.assigned"), color: "bg-blue-500" },
    in_progress: { label: t("worker.status.in_progress"), color: "bg-[#00C4CC]" },
    completed: { label: t("worker.status.completed"), color: "bg-green-600" },
    follow_up: { label: t("worker.status.follow_up"), color: "bg-yellow-500" },
  };
  const priorityMeta = {
    low: { label: t("worker.priority.low"), color: "text-slate-500" },
    normal: { label: t("worker.priority.normal"), color: "text-slate-700" },
    high: { label: t("worker.priority.high"), color: "text-orange-600" },
    urgent: { label: t("worker.priority.urgent"), color: "text-red-600" },
  };
  const [jobs, setJobs] = useState([]);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("today"); // today | prep | all | search
  const [quickOpen, setQuickOpen] = useState(false);
  const [parkingOpen, setParkingOpen] = useState(false);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [query, setQuery] = useState("");
  // Voice-punch for field techs — dictate a walkthrough into a project
  const [voicePunchStep, setVoicePunchStep] = useState(null); // null | "picker" | "recording"
  const [voiceProjects, setVoiceProjects] = useState([]);
  const [voiceProjectsLoading, setVoiceProjectsLoading] = useState(false);
  const [voiceProject, setVoiceProject] = useState(null);

  const openVoicePunch = async () => {
    setVoicePunchStep("picker");
    if (voiceProjects.length === 0) {
      setVoiceProjectsLoading(true);
      try {
        const r = await api.get("/projects");
        setVoiceProjects(r.data?.projects || []);
      } catch (e) {
        toast.error("No projects available yet — ask your PM to create one.");
        setVoicePunchStep(null);
      } finally { setVoiceProjectsLoading(false); }
    }
  };
  const pickVoiceProject = (p) => {
    setVoiceProject(p);
    setVoicePunchStep("recording");
  };
  const closeVoicePunch = () => {
    setVoicePunchStep(null);
    setVoiceProject(null);
  };

  useEffect(() => {
    Promise.all([
      api.get("/jobs"),
      api.get("/auth/me"),
    ]).then(([r1, r2]) => {
      setJobs(r1.data);
      setMe(r2.data);
    }).finally(() => setLoading(false));
  }, []);

  // A WO is "mine" if I'm the assignee OR pinned to my truck on the Sweeper
  // Radar. truck_key for a solo-driver truck is the worker's user_id.
  // Normalize both sides to strings — some legacy WOs stored assigned_to as
  // an ObjectId (BSON) which fails strict === against me.id string.
  const idEq = (a, b) => (a != null && b != null && String(a).trim() === String(b).trim());
  const isMine = (j) => me && (
    idEq(j.assigned_to, me.id) ||
    idEq(j.sweeper_pin?.truck_key, me.id) ||
    idEq(j.helper_id, me.id)
  );
  const isPool = (j) => !j.assigned_to;
  const today = todayISO();

  const openStatuses = ["assigned", "in_progress", "follow_up"];
  const poolStatuses = ["pool", "assigned"]; // pool tab shows unassigned jobs in either state
  const scheduledToday = jobs.filter(j => isMine(j) && scheduledDate(j) === today && openStatuses.includes(j.status));
  const scheduledUpcoming = jobs.filter(j => isMine(j) && scheduledDate(j) > today && openStatuses.includes(j.status));

  const active = jobs.filter(j => j.status === "in_progress" && isMine(j));
  const followUp = jobs.filter(j => j.status === "follow_up" && isMine(j));
  const pending = jobs.filter(j => j.status === "assigned" && isMine(j));
  const pool = jobs.filter(j => poolStatuses.includes(j.status) && isPool(j));
  const done = jobs.filter(j => j.status === "completed" && isMine(j));

  // Global fuzzy search — mine + pool jobs, matches title, address, customer, WO#, unit, notes
  const searchResults = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return jobs
      .filter(j => isMine(j) || isPool(j))
      .filter(j => {
        const hay = [
          j.title, j.property_address, j.unit, j.customer_name, j.customer_phone,
          j.work_order_number, j.description, j.category, j.priority, j.status,
        ].map(v => (v || "").toString().toLowerCase()).join(" | ");
        return q.split(/\s+/).every(tok => hay.includes(tok));
      })
      .slice(0, 60);
  }, [query, jobs, me]);

  return (
    <WorkerShell>
      <div className="px-4 py-6">
        <h1 className="font-display font-black text-4xl tracking-tight">{t("worker.today")}</h1>
        <p className="mt-1 text-slate-500 font-medium">{t("worker.stats", { active: active.length, pool: pool.length, scheduled: scheduledToday.length })}</p>

        {/* Search bar — global job find across mine + pool */}
        <div className="mt-4 relative" data-testid="worker-search-wrapper">
          <MagnifyingGlass weight="bold" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"/>
          <input list="known-addresses" autoComplete="off"
            type="search"
            value={query}
            onChange={e => { setQuery(e.target.value); if (e.target.value) setTab("search"); }}
            placeholder="Search jobs — address, title, WO#, customer, tenant…"
            data-testid="worker-jobs-search"
            className="w-full h-12 pl-10 pr-10 border-2 border-[#0f172a] text-base font-medium focus:outline-none focus:ring-2 focus:ring-[#00C4CC]/40"
          />
          {query && (
            <button onClick={() => { setQuery(""); setTab("today"); }} data-testid="worker-jobs-search-clear"
              className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 flex items-center justify-center text-slate-500 hover:bg-slate-100 rounded">
              <X size={16}/>
            </button>
          )}
        </div>

        {/* Quick Job Here — one-tap on-site */}
        <button onClick={() => setQuickOpen(true)}
          data-testid="quick-job-tile"
          className="tactile-btn mt-4 w-full h-16 bg-gradient-to-r from-amber-400 to-amber-500 text-[#0f172a] font-display font-black uppercase tracking-wide text-lg inline-flex items-center justify-center gap-3 border-2 border-[#0f172a] shadow-[4px_4px_0px_#0f172a]">
          <Lightning weight="fill" size={26}/> Quick Job Here
          <span className="text-[11px] font-black uppercase tracking-widest bg-[#0f172a] text-white px-2 py-0.5 rounded-sm">Bill My Time</span>
        </button>
        <QuickJobModal open={quickOpen} onClose={() => { setQuickOpen(false); Promise.all([api.get("/jobs"), api.get("/auth/me")]).then(([r1, r2]) => { setJobs(r1.data); setMe(r2.data); }); }}/>
        <QuickCaptureModal open={parkingOpen} onClose={() => setParkingOpen(false)} onCreated={() => Promise.all([api.get("/jobs"), api.get("/auth/me")]).then(([r1, r2]) => { setJobs(r1.data); setMe(r2.data); })}/>

        {/* ClosureRadar drop-in — quick capture entry from the field */}
        <button onClick={() => setParkingOpen(true)}
          data-testid="closure-radar-drop-tile"
          className="mt-3 w-full h-12 bg-white text-[#0f172a] font-black uppercase tracking-widest text-xs inline-flex items-center justify-center gap-2 border-2 border-[#0f172a] hover:bg-slate-50">
          📡 Drop To ClosureRadar
        </button>

        {/* Voice → Project Punch — for punch-list walkthroughs */}
        <button onClick={openVoicePunch} data-testid="worker-voice-punch-btn"
          className="mt-3 w-full h-12 bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-black uppercase tracking-widest text-xs inline-flex items-center justify-center gap-2 border-2 border-[#0f172a]">
          <Microphone weight="fill" size={16}/> Voice · Add Punch Items
        </button>

        {/* Turns Board — grab a vacant turn */}
        <Link to="/tech/turns" data-testid="worker-turns-tile"
          className="mt-3 w-full h-12 bg-gradient-to-r from-cyan-500 to-emerald-500 hover:from-cyan-600 hover:to-emerald-600 text-white font-black uppercase tracking-widest text-xs inline-flex items-center justify-center gap-2 border-2 border-[#0f172a]">
          <Broadcast weight="fill" size={16}/> Turns Board · Grab a Turn
        </Link>

        {/* Flag as Purge — bulk trash for truck day */}
        <button onClick={() => setPurgeOpen(true)} data-testid="worker-purge-flag-btn"
          className="mt-3 w-full h-12 bg-amber-500 hover:bg-amber-600 text-slate-900 font-black uppercase tracking-widest text-xs inline-flex items-center justify-center gap-2 border-2 border-[#0f172a]">
          <Trash weight="fill" size={16}/> Flag Bulk Trash · Truck Day
        </button>
        <PurgeFlagModal open={purgeOpen} onClose={() => setPurgeOpen(false)}/>

        {/* Project picker modal for voice-punch flow */}
        {voicePunchStep === "picker" && (
          <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4" data-testid="worker-voice-project-picker">
            <div className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[95vh] flex flex-col">
              <div className="p-4 border-b-2 border-slate-200 flex items-center gap-2">
                <Microphone weight="fill" size={22} className="text-fuchsia-600"/>
                <div className="font-display font-black text-lg uppercase tracking-tight flex-1">Pick a project</div>
                <button onClick={closeVoicePunch} data-testid="worker-voice-picker-close" className="text-slate-500 hover:text-slate-800 h-9 w-9 flex items-center justify-center"><X size={20}/></button>
              </div>
              <div className="p-3 flex-1 overflow-y-auto">
                {voiceProjectsLoading && <div className="text-center py-6 text-sm font-black uppercase tracking-widest text-slate-500">Loading projects…</div>}
                {!voiceProjectsLoading && voiceProjects.length === 0 && (
                  <div className="text-center py-8" data-testid="worker-voice-no-projects">
                    <div className="font-display font-black text-lg">No projects yet</div>
                    <div className="mt-1 text-xs text-slate-500">Your PM needs to create a ProjectRadar project first.</div>
                  </div>
                )}
                <div className="space-y-2">
                  {voiceProjects.map(p => (
                    <button key={p.id || p._id} onClick={() => pickVoiceProject(p)}
                      data-testid={`worker-voice-pick-${p.id || p._id}`}
                      className="w-full text-left p-3 border-2 border-slate-300 hover:border-fuchsia-600 hover:bg-fuchsia-50">
                      <div className="font-black text-sm truncate">{p.name}</div>
                      <div className="text-[10px] text-slate-500 uppercase tracking-widest truncate">{p.property_address} · {p.status}</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {voicePunchStep === "recording" && voiceProject && (
          <VoiceToPunchModal
            projectId={voiceProject.id || voiceProject._id}
            projectName={voiceProject.name}
            rooms={voiceProject.rooms || []}
            onClose={closeVoicePunch}
            onAdded={closeVoicePunch}
          />
        )}

        <div className="mt-5 flex gap-2 border-b-2 border-[#0f172a] overflow-x-auto">
          {[["today", t("worker.tab.today")], ["prep", "Prep Sheet"], ["all", t("worker.tab.all")]].map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`worker-tab-${k}`}
              className={`px-4 py-2 font-black uppercase text-sm tracking-wider inline-flex items-center gap-1 flex-shrink-0 ${
                tab === k ? "bg-[#0f172a] text-white" : "text-slate-600"
              }`}>
              {k === "prep" && <Sun weight={tab === k ? "fill" : "bold"} size={14}/>}
              {label}
              {k === "today" && scheduledToday.length > 0 && (
                <span className="ml-1 px-1.5 py-0.5 text-[10px] bg-[#00C4CC] text-white rounded">{scheduledToday.length}</span>
              )}
            </button>
          ))}
          {query && (
            <button onClick={() => setTab("search")} data-testid="worker-tab-search"
              className={`px-4 py-2 font-black uppercase text-sm tracking-wider inline-flex items-center gap-1 flex-shrink-0 ${
                tab === "search" ? "bg-[#00C4CC] text-white" : "text-slate-600"
              }`}>
              <MagnifyingGlass weight={tab === "search" ? "fill" : "bold"} size={14}/> Search
              <span className="ml-1 px-1.5 py-0.5 text-[10px] bg-white/20 rounded">{searchResults.length}</span>
            </button>
          )}
        </div>

        {loading && <div className="mt-8 font-bold">{t("worker.loading")}</div>}

        {tab === "prep" && !loading && <PrepSheet/>}

        {tab === "search" && !loading && (
          <div className="mt-6" data-testid="worker-search-results">
            <div className="text-xs font-black uppercase tracking-widest text-slate-500">
              {searchResults.length === 0
                ? `No jobs match "${query}"`
                : `${searchResults.length} match${searchResults.length === 1 ? "" : "es"} for "${query}"`}
            </div>
            <div className="mt-4 space-y-4">
              {searchResults.map(j => <JobCard key={j.id} job={j} statusMeta={statusMeta} priorityMeta={priorityMeta}/>)}
            </div>
          </div>
        )}

        {tab === "today" && !loading && (
          <>
            {scheduledToday.length === 0 && scheduledUpcoming.length === 0 && (
              <div className="mt-8 p-8 rugged-card text-center">
                <Calendar weight="bold" size={40} className="mx-auto opacity-40"/>
                <div className="mt-3 font-display font-black text-2xl">{t("worker.empty.title")}</div>
                <p className="mt-2 text-slate-500">{t("worker.empty.sub")}</p>
              </div>
            )}
            {scheduledToday.length > 0 && <Section title={t("worker.section.today")} jobs={scheduledToday} accent="cyan" showSchedule showOptimize statusMeta={statusMeta} priorityMeta={priorityMeta}/>}
            {scheduledUpcoming.length > 0 && <Section title={t("worker.section.upcoming")} jobs={scheduledUpcoming} accent="blue" showSchedule statusMeta={statusMeta} priorityMeta={priorityMeta}/>}
          </>
        )}

        {tab === "all" && !loading && (
          <>
            {jobs.length === 0 && (
              <div className="mt-8 p-8 rugged-card text-center">
                <div className="font-display font-black text-2xl">{t("worker.empty.all.title")}</div>
                <p className="mt-2 text-slate-500">{t("worker.empty.all.sub")}</p>
              </div>
            )}
            {active.length > 0 && <Section title={t("worker.section.on_clock")} jobs={active} accent="cyan" statusMeta={statusMeta} priorityMeta={priorityMeta}/>}
            {followUp.length > 0 && <Section title={t("worker.section.follow_up")} jobs={followUp} accent="yellow" statusMeta={statusMeta} priorityMeta={priorityMeta}/>}
            {pending.length > 0 && <Section title={t("worker.section.assigned")} jobs={pending} accent="blue" statusMeta={statusMeta} priorityMeta={priorityMeta}/>}
            {pool.length > 0 && <Section title={t("worker.section.pool")} jobs={pool} accent="pool" showClaim statusMeta={statusMeta} priorityMeta={priorityMeta}/>}
            {done.length > 0 && <Section title={t("worker.section.done")} jobs={done} accent="grey" statusMeta={statusMeta} priorityMeta={priorityMeta}/>}
          </>
        )}
      </div>
    </WorkerShell>
  );
}


function Section({ title, jobs, accent, showClaim, showSchedule, showOptimize, statusMeta, priorityMeta }) {
  const [ordered, setOrdered] = useState(null);
  const dot = accent === "cyan" ? "bg-[#00C4CC]" : accent === "blue" ? "bg-blue-500" : accent === "yellow" ? "bg-yellow-500" : accent === "pool" ? "bg-orange-500" : "bg-slate-400";
  const optimize = async () => {
    const stops = jobs.filter(j => j.geocoded).map(j => ({ id: j.id, lat: j.geocoded.lat, lng: j.geocoded.lng, title: j.title, addr: j.property_address }));
    if (stops.length < 2) { toast.info("Need at least 2 jobs with map coordinates."); return; }
    try {
      const { data } = await import("@/lib/api").then(m => m.api.post("/routes/optimize", { stops }));
      setOrdered({ list: data.ordered, km: data.total_km });
      toast.success(`Optimized ${data.ordered.length} stops · ${data.total_km} km total`);
    } catch (e) { toast.error("Route optimization failed"); }
  };
  const mapsLink = () => {
    if (!ordered) return "";
    const addrs = ordered.list.map(s => `${s.lat},${s.lng}`).join("/");
    return `https://www.google.com/maps/dir/${addrs}`;
  };
  return (
    <section className="mt-8">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-display font-black text-xl uppercase tracking-wide flex items-center gap-2">
          <span className={`inline-block h-3 w-3 ${dot}`}/>
          {title} <span className="text-slate-400">({jobs.length})</span>
        </h2>
        {showOptimize && (
          <div className="flex gap-2">
            <button onClick={optimize} data-testid="optimize-route-btn"
              className="tactile-btn h-10 px-3 bg-[#0f172a] text-white font-black uppercase text-xs tracking-wider">
              Optimize Route
            </button>
            {ordered && (
              <a href={mapsLink()} target="_blank" rel="noreferrer" data-testid="open-in-maps"
                className="tactile-btn h-10 px-3 bg-[#00C4CC] text-white font-black uppercase text-xs tracking-wider">
                Open in Maps · {ordered.km}km
              </a>
            )}
          </div>
        )}
      </div>
      {ordered && (
        <ol className="mt-3 list-decimal pl-6 text-sm space-y-0.5 font-medium">
          {ordered.list.map((s, i) => <li key={s.id}>{s.title} — <span className="text-slate-500">{s.addr}</span></li>)}
        </ol>
      )}
      <div className="mt-4 space-y-4">
        {jobs.map(j => <JobCard key={j.id} job={j} showClaim={showClaim} showSchedule={showSchedule} statusMeta={statusMeta} priorityMeta={priorityMeta}/>)}
      </div>
    </section>
  );
}

function JobCard({ job, showClaim, showSchedule, statusMeta, priorityMeta }) {
  const { t } = useT();
  const s = statusMeta[job.status] || statusMeta.assigned;
  const p = priorityMeta[job.priority] || priorityMeta.normal;
  const checklistTotal = (job.checklist || []).length;
  const checklistDone = (job.checklist || []).filter(c => c.done).length;
  const sched = scheduledDate(job);
  return (
    <Link to={`/worker/jobs/${job.id}`} data-testid={`job-card-${job.id}`} className="block rugged-card p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {showClaim ? (
              <span className="px-2 py-0.5 text-xs font-black uppercase tracking-wider text-white bg-orange-500 inline-flex items-center gap-1">
                <HandGrabbing weight="bold" size={12}/> {t("worker.unclaimed")}
              </span>
            ) : (
              <span className={`px-2 py-0.5 text-xs font-black uppercase tracking-wider text-white ${s.color}`}>{s.label}</span>
            )}
            <span className={`text-xs font-black tracking-wider ${p.color}`}>{p.label}</span>
            {checklistTotal > 0 && (
              <span className="text-xs font-black tracking-wider text-slate-500">
                {checklistDone}/{checklistTotal} {t("worker.issues")}
              </span>
            )}
            {showSchedule && sched && (
              <span className="text-xs font-black tracking-wider text-[#00C4CC] inline-flex items-center gap-1">
                <Calendar weight="bold" size={12}/> {sched}
              </span>
            )}
          </div>
          <h3 className="mt-2 font-display font-black text-xl tracking-tight truncate">{job.title}</h3>
          <div className="mt-1.5">
            <AITriageButton jobId={job.id} triage={job.ai_triage || null} compact/>
          </div>
          {job.work_order_number && (
            <div className="mt-1 inline-flex items-center px-2 py-0.5 bg-[#0f172a] text-white text-[10px] font-black uppercase tracking-widest font-mono"
              data-testid={`worker-card-wo-${job.id}`}>
              WO# {job.work_order_number}
            </div>
          )}
          <div className="mt-1 flex items-center gap-1.5 text-sm text-slate-600 font-medium">
            <MapPin weight="bold" size={14}/> <span className="truncate">{job.property_address}</span>
          </div>
          {job.customer_name && <div className="mt-1 text-sm text-slate-500">{t("worker.customer")}: {job.customer_name}</div>}
          {job.clock_in_at && !job.clock_out_at && (
            <div className="mt-2 flex items-center gap-1.5 text-xs text-[#00C4CC] font-black uppercase">
              <Clock weight="bold" size={14}/> {t("worker.clocked_in")}
            </div>
          )}
        </div>
        <CaretRight weight="bold" size={24}/>
      </div>
    </Link>
  );
}
